import {
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  CALENDLY_EVENT_TYPES_QUERY_KEY,
  CALENDLY_EVENT_TYPES_QUERY_OPTIONS,
  getCalendlyEventTypes,
  setDefaultCalendlyEventType,
} from "../../api/calendly";
import FlexBox from "../layouts/Flexbox";
import { useMutationWithDispatch } from "../../hooks/useMutationWithDispatch";
import { useQueryWithDispatch } from "../../hooks/useQueryWithDispatch";

type DefaultCalendlyEventTypeSelectorProps = {
  enabled?: boolean;
  label?: string;
  description?: string;
  formLabelId?: string;
};

export function DefaultCalendlyEventTypeSelector({
  enabled = true,
  label = "Default event type",
  description,
  formLabelId = "default-calendly-event-type-label",
}: DefaultCalendlyEventTypeSelectorProps) {
  const [selectedEventTypeUri, setSelectedEventTypeUri] = useState("");
  const queryClient = useQueryClient();

  const {
    data: eventTypesResponse,
    isPending: fetchingEventTypes,
    isError: eventTypesError,
  } = useQueryWithDispatch({
    queryKey: CALENDLY_EVENT_TYPES_QUERY_KEY,
    thunkAction: getCalendlyEventTypes,
    variables: undefined,
    enabled,
    showErrorToast: false,
    ...CALENDLY_EVENT_TYPES_QUERY_OPTIONS,
  });

  const eventTypes = useMemo(
    () => eventTypesResponse?.data?.eventTypes ?? [],
    [eventTypesResponse]
  );

  const defaultEventTypeUri = useMemo(() => {
    const defaultFromList = eventTypes.find((event) => event.isDefault)?.uri;
    return (
      defaultFromList ?? eventTypesResponse?.data?.defaultEventTypeUri ?? ""
    );
  }, [eventTypes, eventTypesResponse]);

  useEffect(() => {
    setSelectedEventTypeUri(defaultEventTypeUri);
  }, [defaultEventTypeUri]);

  const { mutateAsync: updateDefaultEventType, isPending: updatingDefault } =
    useMutationWithDispatch(setDefaultCalendlyEventType, {
      showErrorToast: false,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: CALENDLY_EVENT_TYPES_QUERY_KEY,
        });
      },
    });

  const onDefaultEventTypeChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const nextUri = event.target.value;

    if (!nextUri || nextUri === selectedEventTypeUri) {
      return;
    }

    const previousUri = selectedEventTypeUri;
    setSelectedEventTypeUri(nextUri);

    try {
      await updateDefaultEventType(nextUri);
    } catch {
      setSelectedEventTypeUri(previousUri);
    }
  };

  if (!enabled) {
    return null;
  }

  return (
    <FormControl fullWidth>
      <FormLabel id={formLabelId}>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
      </FormLabel>

      {description && (
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {description}
        </Typography>
      )}

      {fetchingEventTypes ? (
        <FlexBox alignItems="center" gap={1} mt={1}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading event types...
          </Typography>
        </FlexBox>
      ) : eventTypesError ? (
        <Typography variant="body2" color="error" mt={1}>
          Unable to load Calendly event types.
        </Typography>
      ) : eventTypes.length === 0 ? (
        <Typography variant="body2" color="text.secondary" mt={1}>
          No Calendly event types found.
        </Typography>
      ) : (
        <RadioGroup
          aria-labelledby={formLabelId}
          name={formLabelId}
          value={selectedEventTypeUri}
          onChange={onDefaultEventTypeChange}
        >
          {eventTypes.map((eventType) => (
            <FormControlLabel
              key={eventType.uri}
              value={eventType.uri}
              disabled={updatingDefault}
              control={<Radio />}
              label={
                <FlexBox alignItems="center" gap={1}>
                  <Typography variant="body2">{eventType.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {eventType.duration} min
                  </Typography>
                </FlexBox>
              }
            />
          ))}
        </RadioGroup>
      )}
    </FormControl>
  );
}
